import { interval, of, } from 'rxjs';
import { exhaustMap, first, startWith, timeoutWith, } from 'rxjs/operators';
import Vue from 'vue';
import VueMeta from 'vue-meta';
import VueRouter from 'vue-router';
import Login from '~/pages/login/index.vue';
import { ChangeEmailAddressRoutes, } from './change-email-address/routes';
import { EbillingRegisterRoutes, } from './digital-billing/routes';
import { DirectDebitRoutes, } from './direct-debit/routes';
import { FeedbackSurveyRoutes, } from './feedback-survey/routes';
import { FindCustomerNumberRoutes, } from './find-customer-number/routes';
import { ForgotEmailRoutes, } from './forgot-email/routes';
import { MeterReadRoutes, } from './meter-read/routes';
import { MoveOutRoutes, } from './move-out/routes';
import { MoveWithinRoutes, } from './move-within/routes';
import Dashboard from './pages/dashboard.vue';
import { PayBillRoutes, } from './pay-bill/routes';
import { ProfileRoutes, } from './profile/routes';
import { RegisterRoutes, } from './register/routes';
import { Store, } from './store';

Vue.use(VueRouter);
Vue.use(VueMeta);

const prefix = '/your-account';

export const Router = new VueRouter({
	base: prefix,
	mode: 'history',
	scrollBehavior() {
		return { x: 0, y: 0, };
	},
	routes: [
		{ path: `/`, component: Dashboard, name: 'Dashboard', meta: { enableLiveChatTab: true, }, },
		{ path: `/login`, component: Login, name: 'Login', meta: { bypassPreventLeaveMixin: true, }, },
		{ path: '/logout', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/logout.vue').then((m) => m.default), name: 'Logout', },
		{
			path: `/reset-password`,
			component: () => import(/* webpackChunkName: "chunk-your-account-reset-password" */'~/pages/reset-password/master.vue').then((m) => m.default),
			children: [
				{ path: `security-question`, component: () => import(/* webpackChunkName: "chunk-your-account-reset-password" */'~/pages/reset-password/security-question.vue').then((m) => m.default), },
				{ path: `create-password`, component: () => import(/* webpackChunkName: "chunk-your-account-reset-password" */'~/pages/reset-password/create-password.vue').then((m) => m.default), },
				{ path: `verify-email`, component: () => import(/* webpackChunkName: "chunk-your-account-reset-password" */'~/pages/reset-password/verify-email.vue').then((m) => m.default), name: 'Reset Password Verify Email', },
				{ path: `password-confirmation`, component: () => import(/* webpackChunkName: "chunk-your-account-reset-password" */'~/pages/reset-password/password-confirmation.vue').then((m) => m.default), name: 'passwordConfirmation', },
				{ path: '', component: () => import(/* webpackChunkName: "chunk-your-account-reset-password" */'~/pages/reset-password/reset-password.vue').then((m) => m.default), name: 'Reset Password', },
			],
		},
		...MeterReadRoutes,
		...MoveOutRoutes,
		...PayBillRoutes,
		...RegisterRoutes,
		...EbillingRegisterRoutes,
		...DirectDebitRoutes,
		...MoveWithinRoutes,
		...ProfileRoutes,
		...FeedbackSurveyRoutes,
		...FindCustomerNumberRoutes,
		...ForgotEmailRoutes,
		...ChangeEmailAddressRoutes,
		{ path: `/paperless-preference`, name: 'PaperlessPreference', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/paperless-preference.vue').then((m) => m.default), },
		{ path: `/payment-history`, name: 'PaymentHistory', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/payment-history.vue').then((m) => m.default), },
		{ path: `/account-statement`, name: 'AccountStatement', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/account-statement.vue').then((m) => m.default), },
		{ path: `/you-are-moving`, name: 'TellUsYourMoving', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */ './pages/tell-us-your-moving.vue').then((m) => m.default), },
		{ path: `/billing-history`, name: 'BillingHistory', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/billing-history.vue').then((m) => m.default), },
		{ path: `/no-pdf-found`, name: 'NoPdfFound', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/no-pdf-found.vue').then((m) => m.default), },
		{ path: `/view-correspondence`, name: 'ViewCorrespondence', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/view-correspondence.vue').then((m) => m.default), },
		{ path: `/switch-account`, name: 'SwitchAccount', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/switch-account.vue').then((m) => m.default), },
		{ path: `/correspondence-not-found`, name: 'NoCorrespondenceFound', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/no-correspondence-found.vue').then((m) => m.default), },
		{ path: `/water-usage`, name: 'WaterUsage', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/water-usage.vue').then((m) => m.default), },
		{ path: `/select-meter`, name: 'SelectMeter', component: () => import(/* webpackChunkName: "chunk-your-account-dashboard" */'./pages/select-meter.vue').then((m) => m.default), },
	],
});

function setLiveChatVisbility(visible: boolean) {
	const evt = visible ? 'livechat:show' : 'livechat:hide';
	document.dispatchEvent(new Event(evt));
}

Router.afterEach((to) => {
	const { enableLiveChatTab = false, } = (to.meta || {}) as Record<string, unknown>;
	setLiveChatVisbility(enableLiveChatTab === true);
});

Router.afterEach((to) => {
	Store.commit.setActiveAlert(to.fullPath);
});

Router.beforeEach(async (to, _from, next) => {
	Store.commit.clearReadMessage();
	// redirect to login page if not logged in and trying to access a restricted page
	const publicPages = [
		'/login',
		'/register',
		'/digital-billing',
		'/reset-password',
		'/find-customer-number',
		'/forgot-email',
		'/change-email-address',
	];

	const isPublic = publicPages.some((path) => to.fullPath.startsWith(path));
	const authRequired = !isPublic;
	let loggedIn = Store.getters.isAuthenticated;
	let attemptedFetch = false;

	if (!Store.state.prefetchedCustomerDetails) { // can't trust the getter if not prefetched
		attemptedFetch = true;
		loggedIn = await new Promise((resolve) => {
			interval(100)
				.pipe(
					startWith(0),
					exhaustMap(() => of(Store.state.prefetchedCustomerDetails)),
					first((value) => value === true),
					timeoutWith(5000, of(true)),
				)
				.subscribe((isPreFetched) => resolve(isPreFetched ? Store.getters.isAuthenticated : false));
		});
	}

	if (authRequired && !loggedIn) {
		Store.commit.setPostLoginPath(to.fullPath);
		if (attemptedFetch) {
			Store.commit.reset();
			Store.commit.showMessage({ type: 'Warning', body: 'We have been unable to verify your session, for security reasons please log in again', });
			Store.commit.prepareAvailableMessage();
		}
		return next(to.fullPath !== '/' ? `/login?redirectUrl=${encodeURIComponent(to.fullPath)}` : '/login');
	}

	if (!authRequired && loggedIn) {
		return next('/');
	}

	Store.commit.prepareAvailableMessage();
	next();
});
